import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { adminService } from '../../../services/adminService';


const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [messageSubject, setMessageSubject] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadUsers();
  }, [filter, roleFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await adminService?.getAllUsers({
        role: roleFilter,
        status: filter
      });
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'active': return { label: 'Active', color: 'bg-success/10 text-success', icon: 'CheckCircle' };
      case 'suspended': return { label: 'Suspended', color: 'bg-error/10 text-error', icon: 'Ban' };
      case 'pending': return { label: 'Pending', color: 'bg-warning/10 text-warning', icon: 'Clock' };
      default: return { label: 'Active', color: 'bg-success/10 text-success', icon: 'CheckCircle' };
    }
  };

  const handleSuspend = async (userId) => {
    setActionLoading(true);
    try {
      await adminService?.suspendUser(userId);
      await loadUsers();
      showToast('User suspended successfully');
    } catch (error) {
      showToast('Failed to suspend user', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async (userId) => {
    setActionLoading(true);
    try {
      await adminService?.activateUser(userId);
      await loadUsers();
      showToast('User activated successfully');
    } catch (error) {
      showToast('Failed to activate user', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenMessage = (user) => {
    setMessageTarget(user);
    setMessageContent('');
    setMessageSubject('');
    setShowMessageModal(true);
  };

  const handleSendMessage = async () => {
    if (!messageContent?.trim() || !messageTarget) return;
    setSendingMessage(true);
    try {
      await adminService?.sendMessageToUser(messageTarget?.id, messageContent, messageSubject || 'Message from Platform Admin');
      setShowMessageModal(false);
      setMessageContent('');
      setMessageSubject('');
      showToast(`Message sent to ${messageTarget?.fullName || messageTarget?.email}`);
    } catch (error) {
      showToast('Failed to send message. ' + (error?.message || ''), 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesSearch;
  });

  const totalUsers = users?.length;
  const activeUsers = users?.filter(u => !u?.accountStatus || u?.accountStatus === 'active')?.length;
  const suspendedUsers = users?.filter(u => u?.accountStatus === 'suspended')?.length;
  const verifiedUsers = users?.filter(u => u?.isVerified || u?.verificationStatus === 'verified')?.length;

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg font-body text-sm font-medium ${
          toast?.type === 'error' ? 'bg-error text-white' : 'bg-success text-white'
        }`}>
          {toast?.message}
        </div>
      )}
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="Users" size={20} strokeWidth={2} className="text-primary" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{totalUsers}</p>
          <p className="font-caption text-sm text-muted-foreground">Total Users</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="CheckCircle" size={20} strokeWidth={2} className="text-success" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{activeUsers}</p>
          <p className="font-caption text-sm text-muted-foreground">Active Users</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="Ban" size={20} strokeWidth={2} className="text-error" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{suspendedUsers}</p>
          <p className="font-caption text-sm text-muted-foreground">Suspended</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="ShieldCheck" size={20} strokeWidth={2} className="text-accent" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{verifiedUsers}</p>
          <p className="font-caption text-sm text-muted-foreground">Verified</p>
        </div>
      </div>
      {/* Search and Filter */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="relative flex-1 max-w-md">
            <Icon name="Search" size={18} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 bg-muted border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e?.target?.value)}
              className="px-3 py-2 bg-muted border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Roles</option>
              <option value="owner">Owners</option>
              <option value="renter">Renters</option>
              <option value="admin">Admins</option>
            </select>
            {['all', 'active', 'suspended']?.map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-smooth capitalize ${
                  filter === status ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {status}
              </button>
            ))}
            <Button variant="outline" size="sm" onClick={loadUsers}>
              <Icon name="RefreshCw" size={14} strokeWidth={2} className="mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </div>
      {/* Users List */}
      <div className="bg-card rounded-lg shadow-elevation-2">
        <div className="p-6 border-b border-border">
          <h3 className="font-heading text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Users" size={20} strokeWidth={2} className="text-primary" />
            <span>User Accounts ({filteredUsers?.length})</span>
          </h3>
        </div>
        <div className="divide-y divide-border">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="font-body text-muted-foreground">Loading users...</p>
            </div>
          ) : filteredUsers?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Inbox" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-3" />
              <p className="font-body text-muted-foreground">No users found</p>
            </div>
          ) : (
            filteredUsers?.map((user) => {
              const statusConfig = getStatusConfig(user?.accountStatus);
              return (
                <div key={user?.id} className="p-5 hover:bg-muted/50 transition-smooth">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <h4 className="font-body font-semibold text-foreground">{user?.fullName || 'Unknown'}</h4>
                        {(user?.isVerified || user?.verificationStatus === 'verified') && (
                          <span className="flex items-center space-x-1 text-accent">
                            <Icon name="ShieldCheck" size={14} strokeWidth={2} />
                            <span className="text-xs font-medium">Verified</span>
                          </span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.color} flex items-center space-x-1`}>
                          <Icon name={statusConfig?.icon} size={12} strokeWidth={2} />
                          <span>{statusConfig?.label}</span>
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          user?.role === 'owner' ? 'bg-blue-500/10 text-blue-600' :
                          user?.role === 'admin'? 'bg-purple-500/10 text-purple-600' : 'bg-green-500/10 text-green-600'
                        }`}>
                          {user?.role || 'renter'}
                        </span>
                      </div>
                      <p className="font-body text-sm text-muted-foreground mb-2">{user?.email}</p>
                      <div className="flex items-center flex-wrap gap-3 text-xs text-muted-foreground">
                        {user?.phone && (
                          <span className="flex items-center space-x-1">
                            <Icon name="Phone" size={12} strokeWidth={2} />
                            <span>{user?.phone}</span>
                          </span>
                        )}
                        <span className="flex items-center space-x-1">
                          <Icon name="Calendar" size={12} strokeWidth={2} />
                          <span>Joined {new Date(user?.createdAt)?.toLocaleDateString()}</span>
                        </span>
                        {user?.locationCity && (
                          <span className="flex items-center space-x-1">
                            <Icon name="MapPin" size={12} strokeWidth={2} />
                            <span>{user?.locationCity}, {user?.locationState}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenMessage(user)}
                        title="Send message to user"
                      >
                        <Icon name="MessageSquare" size={14} strokeWidth={2} />
                        <span className="ml-1 hidden sm:inline">Message</span>
                      </Button>
                      {user?.accountStatus === 'suspended' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={actionLoading}
                          onClick={() => handleActivate(user?.id)}
                          className="text-success hover:bg-success/10"
                        >
                          <Icon name="CheckCircle" size={14} strokeWidth={2} />
                          <span className="ml-1 hidden sm:inline">Activate</span>
                        </Button>
                      ) : (
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={actionLoading}
                          onClick={() => handleSuspend(user?.id)}
                        >
                          <Icon name="Ban" size={14} strokeWidth={2} />
                          <span className="ml-1 hidden sm:inline">Suspend</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {/* Send Message Modal */}
      {showMessageModal && messageTarget && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-lg">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-foreground flex items-center space-x-2">
                <Icon name="MessageSquare" size={20} strokeWidth={2} className="text-primary" />
                <span>Send Message to {messageTarget?.fullName || messageTarget?.email}</span>
              </h3>
              <button onClick={() => setShowMessageModal(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} strokeWidth={2} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="font-body text-sm font-medium text-foreground mb-1 block">Subject</label>
                <input
                  type="text"
                  value={messageSubject}
                  onChange={(e) => setMessageSubject(e?.target?.value)}
                  placeholder="Message subject..."
                  className="w-full px-3 py-2 bg-muted border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="font-body text-sm font-medium text-foreground mb-1 block">Message</label>
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e?.target?.value)}
                  placeholder="Type your message here..."
                  rows={5}
                  className="w-full px-3 py-2 bg-muted border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
              <div className="flex items-center justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowMessageModal(false)}>Cancel</Button>
                <Button
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !messageContent?.trim()}
                >
                  {sendingMessage ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Icon name="Send" size={16} strokeWidth={2} className="mr-2" />
                  )}
                  Send Message
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;